class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.refreshVersion = 0;
    this.addEventListener('keyup', event => {
      if (event.code === 'Escape') this.close();
    });
    this.addEventListener('click', event => {
      if (event.target.closest('#CartDrawer-Overlay')) this.close();
      const button = event.target.closest('[data-kart-suggestion-id]');
      if (button) this.addSuggestion(button);
    });
    this.headerClick = event => {
      const link = event.target.closest('#cart-icon-bubble');
      if (!link) return;
      event.preventDefault();
      this.open(link);
      this.refresh().catch(error => this.showRefreshError(error));
    };
    this.cartUpdated = () => {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => {
        this.refresh().catch(error => this.showRefreshError(error));
      }, 120);
    };
  }

  connectedCallback() {
    document.addEventListener('click', this.headerClick);
    document.addEventListener('cart:updated', this.cartUpdated);
    this.setHeaderCartIconAccessibility();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.headerClick);
    document.removeEventListener('cart:updated', this.cartUpdated);
    clearTimeout(this.refreshTimer);
  }

  setHeaderCartIconAccessibility() {
    const link = document.getElementById('cart-icon-bubble');
    if (!link) return;
    link.setAttribute('aria-haspopup', 'dialog');
    // Keep the native link keyboard behavior. Delegation survives HTML updates.
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const note = this.querySelector('[id^="Details-"] summary');
    if (note && !note.hasAttribute('role')) this.setSummaryAccessibility(note);
    this.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
    requestAnimationFrame(() => {
      if (!this.classList.contains('active')) return;
      const container = this.querySelector('.drawer__inner');
      if (container) trapFocus(container, this.querySelector('.drawer__close') || container);
    });
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(note) {
    note.setAttribute('role', 'button');
    note.setAttribute('aria-expanded', String(note.parentElement.open));
    if (note.nextElementSibling?.id) note.setAttribute('aria-controls', note.nextElementSibling.id);
    note.addEventListener('click', () => {
      note.setAttribute('aria-expanded', String(!note.parentElement.open));
    });
    note.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async refresh() {
    const version = ++this.refreshVersion;
    this.setAttribute('aria-busy', 'true');
    try {
      const url = new URL(window.location.href);
      url.search = '';
      url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
      const response = await fetch(url.toString(), {cache: 'no-store'});
      if (!response.ok) throw new Error('Не успяхме да обновим количката.');
      const sections = await response.json();
      if (version !== this.refreshVersion) return;
      this.applySections(sections);
    } finally {
      if (version === this.refreshVersion) this.removeAttribute('aria-busy');
    }
  }

  applySections(sections) {
    // Validate both fragments before replacing any visible content.
    const drawer = this.getSectionDOM(sections?.['cart-drawer'], '#CartDrawer');
    const icon = this.getSectionDOM(sections?.['cart-icon-bubble'], '.shopify-section');
    if (!drawer || !icon) throw new Error('Липсват данни за обновяване на количката.');
    const wasOpen = this.classList.contains('active');
    const focusId = this.contains(document.activeElement) ? document.activeElement.id : '';
    const drawerTarget = this.querySelector('#CartDrawer');
    const iconTarget = document.getElementById('cart-icon-bubble');
    if (!drawerTarget || !iconTarget) throw new Error('Липсва елемент на количката.');
    if (wasOpen) removeTrapFocus();
    drawerTarget.innerHTML = drawer.innerHTML;
    iconTarget.innerHTML = icon.innerHTML;
    this.classList.toggle('is-empty', !!this.querySelector('.drawer__inner-empty'));
    this.setHeaderCartIconAccessibility();
    if (wasOpen) {
      this.open();
      requestAnimationFrame(() => {
        const focusTarget = focusId ? document.getElementById(focusId) : null;
        if (focusTarget && this.contains(focusTarget)) focusTarget.focus();
      });
    }
  }

  async renderContents(parsedState) {
    try {
      ++this.refreshVersion;
      this.applySections(parsedState.sections);
      this.open();
    } catch (error) {
      try {
        await this.refresh();
        this.open();
      } catch (refreshError) {
        this.showRefreshError(refreshError);
      }
    }
  }

  showRefreshError(error) {
    console.error(error);
    const target = this.querySelector('#CartDrawer-CartErrors');
    if (target) target.textContent = 'Количката не се обнови. Презаредете страницата, преди да добавяте отново.';
    this.open();
  }

  async addSuggestion(button) {
    if (button.disabled) return;
    const label = button.textContent;
    const status = this.querySelector('[data-kart-suggestions-status]');
    button.disabled = true;
    button.textContent = 'Добавяне…';
    if (status) status.textContent = '';
    try {
      const root = window.Shopify?.routes?.root || '/';
      // Do not route suggestion additions through the product personalization fetch wrapper.
      const result = await new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        request.open('POST', root + 'cart/add.js');
        request.setRequestHeader('Content-Type', 'application/json');
        request.setRequestHeader('Accept', 'application/json');
        request.timeout = 20000;
        request.onload = () => {
          try {
            const data = JSON.parse(request.responseText);
            if (request.status < 200 || request.status >= 300 || data.status) {
              reject(new Error(data.description || 'Неуспешно добавяне.'));
            } else resolve(data);
          } catch (error) { reject(error); }
        };
        request.onerror = request.ontimeout = () => reject(new Error('Неясен резултат. Проверете количката, преди да добавяте отново.'));
        request.send(JSON.stringify({
          items: [{id: button.dataset.kartSuggestionId, quantity: 1}],
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname
        }));
      });
      await this.renderContents(result);
    } catch (error) {
      if (status?.isConnected) status.textContent = error.message || 'Възникна грешка. Проверете количката преди нов опит.';
    } finally {
      if (button.isConnected) {
        button.disabled = false;
        button.textContent = label;
      }
    }
  }

  getSectionsToRender() {
    return [{id: 'cart-drawer', selector: '#CartDrawer'}, {id: 'cart-icon-bubble'}];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    if (typeof html !== 'string') return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return this.getSectionDOM(html, selector)?.innerHTML || '';
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner'},
      {id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section'}
    ];
  }
}

if (!customElements.get('cart-drawer-items')) customElements.define('cart-drawer-items', CartDrawerItems);
